import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { getSession, useGetAllProfileQuery } from '@onesource/services';
import { isFeatureFlagEnabled } from '@onesource/components';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { FC, SyntheticEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { Iconify } from 'src/components/iconify';
import { EFeatureFlagEnum } from 'src/hooks/use-feature-flag-utils';
import { CONFIG } from 'src/config-global';
import { ProfileTab } from './account-profile-tab';
import { SecurityTab } from './account-security-tab';
import { TeachersDirectoryTab } from './account-teachers-directory-tab';
import { PageContainer } from '#/components/page-container';

// ----------------------------------------------------------------------

const ALL_TAB_KEYS = ['profile', 'security', 'teachers-directory'] as const;
type TTabKey = (typeof ALL_TAB_KEYS)[number];

export const AccountPage: FC = () => {
  const token = getSession();
  const { data } = useGetAllProfileQuery({ token });
  const {
    firstName,
    lastName,
    email,
    avatar,
    displayName,
    username,
    timezone,
    occupation,
    bio,
    primaryLanguage,
    phoneNumber,
    zoomEmail,
    address,
    socials,
    isPrivate,
    isTeacher,
  } = data || {};

  const { tab } = useSearch({ from: '/account' });
  const navigate = useNavigate({ from: '/account' });

  const showTeacherDirectory = isFeatureFlagEnabled(EFeatureFlagEnum.SHOW_TEACHER_DIRECTORY);
  const tabKeys: TTabKey[] = showTeacherDirectory
    ? ['profile', 'security', 'teachers-directory']
    : ['profile', 'security'];

  const requestedTab = (tab ?? 'profile') as TTabKey;
  const activeTabIndex = Math.max(0, tabKeys.indexOf(requestedTab));

  const handleTabChange = (_: SyntheticEvent, value: number) => {
    navigate({ search: { tab: tabKeys[value] }, replace: true });
  };

  return (
    <>
      <Helmet>
        <title> {`My Account - ${CONFIG.appName}`}</title>
      </Helmet>
      <PageContainer>
        <Container maxWidth="xl">
          <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
            My Account
          </Typography>

          <Tabs value={activeTabIndex} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Profile" icon={<Iconify icon="solar:user-bold-duotone" width={20} />} iconPosition="start" />
            <Tab label="Security" icon={<Iconify icon="solar:lock-bold-duotone" width={20} />} iconPosition="start" />
            {showTeacherDirectory && (
              <Tab
                label="Teacher's Directory"
                icon={<Iconify icon="solar:notebook-bookmark-bold-duotone" width={20} />}
                iconPosition="start"
              />
            )}
          </Tabs>
          <Box sx={{ height: { xs: 'calc(100vh - 235px)', md: 'calc(100vh - 250px)' }, overflowY: 'auto' }}>
            <Box sx={{ display: tabKeys[activeTabIndex] === 'profile' ? 'block' : 'none' }}>
              {data && (
                <ProfileTab
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  avatar={avatar}
                  displayName={displayName}
                  username={username}
                  timezone={timezone}
                  occupation={occupation}
                  bio={bio}
                  primaryLanguage={primaryLanguage}
                  phoneNumber={phoneNumber}
                  zoomEmail={zoomEmail}
                  address={address}
                  socials={socials}
                  isPrivate={isPrivate}
                />
              )}
            </Box>

            {showTeacherDirectory && (
              <Box sx={{ display: tabKeys[activeTabIndex] === 'teachers-directory' ? 'block' : 'none' }}>
                <TeachersDirectoryTab isTeacher={!!isTeacher} />
              </Box>
            )}

            <Box sx={{ display: tabKeys[activeTabIndex] === 'security' ? 'block' : 'none' }}>
              <SecurityTab />
            </Box>
          </Box>
        </Container>
      </PageContainer>
    </>
  );
};
